import { LayoutGrid, Menu, Trash2 } from 'lucide-react';
import { OpportunityRow } from 'bindings/opportunities/OpportunityRow';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import { AUsers } from '../../atoms/global/users';
import ClientCompany from '../../components_v2/clientCompany/ClientCompany';
import OpportunityCreation from '../../components_v2/creation/OpportunityCreation';
import Add from '../../components_v2/add/Add';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import { FilterId, filterTreeLength } from '../../components_v2/filter/model/Model';
import { FilterParameter } from '../../components_v2/filter/pages/FilterList';
import InputSearch from '../../components_v2/input/InputSearch';
import Pagination from '../../components_v2/pagination/Pagination';
import { PaginationResult } from '../../components_v2/pagination/model/Model';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import { Table, TableSortType } from '../../components_v2/table/Table';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import { TagType } from '../../components_v2/toolbarFilter/tags/Model';
import { getTags, updateTags } from '../../components_v2/toolbarFilter/tags/action';
import { removeAllUrlParameters } from '../../components_v2/utils';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { RedSidely } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import { checkUrlModalParams } from '../client-companies/Companies';
import { DEFAULT_FILTER_RESULT } from '../client-companies/CompaniesAndMapView';
import { SelectionAction, SelectionActionContainer } from '../client-companies/style/Style';
import { FilterState } from '../contact/Contacts';
import { TableContext, TableProvider } from '../contact/data/ContactContext';
import { TOOLBARS_HEIGHT, ToolbarBox } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import { IconsGroupBackground, SwapImageContainer } from '../map/PageSwitch';
import NoData, { Unhautorized } from '../noData/NoData';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import { FlexDiv } from '../products/style';
import { QueryUpdateOpportunity, deleteOpportunity, getOpportunity, getOpportunityFilters, getOpportunityStatuses, queryOpportunities, updateOpportunity } from './data/Data';
import OpportunityColumns, { OPPORTUNITY_HIDDEN_LOCAL_STORAGE, OPPORTUNITY_SORT_LOCAL_STORAGE } from './data/OpportunityColumns';
import { OpportunityStatus } from './model/Model';
import OpportunityKanban from './OpportunityKanban';
import OpportunityPopup from './popup/OpportunityPopup';

const DEFAULT_STEP = 25;
// shortcut: the kanban loads at most this many opportunities, add per-column paging if companies exceed it
const KANBAN_LIMIT = 1000;
const VIEW_LOCAL_STORAGE = 'opportunity_view';
type View = 'list' | 'kanban';

export default function OpportunitiesWrapper(props: ComponentProps) {
	return <TableProvider>
		<Opportunities {...props} />
	</TableProvider>;
}

function Opportunities(props: ComponentProps): JSX.Element {
	const [opportunities, setOpportunities] = React.useState<OpportunityRow[]>([]);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [pagination, setPagination] = React.useState<PaginationResult>();
	const [sort, setSort] = React.useState<TableSortType | undefined>({ id: 'created_at', desc: true });
	const [search, setSearch] = React.useState<string>();
	const [selected, setSelected] = React.useState<Set<number>>(new Set());
	const [isBulkDeleteOpen, setIsBulkDeleteOpen] = React.useState(false);
	const [isCreationOpen, setIsCreationOpen] = React.useState(false);
	const [filterState, setFilterState] = React.useState<FilterState>({});
	const [filterParams, setFilterParams] = React.useState<FilterParameter[]>();
	const [modalState, setModalState] = React.useState<ModalState<{ id: number }>>({ isOpen: false });
	const [companyState, setCompanyState] = React.useState<ModalState<number>>({ isOpen: false });
	const [statuses, setStatuses] = React.useState<OpportunityStatus[]>([]);
	const [tags, setTags] = React.useState<ITag[]>([]);
	const [view, setView] = useFunctionState<View>(localStorage.getItem(VIEW_LOCAL_STORAGE) === 'kanban' ? 'kanban' : 'list', ({ newValue }) => {
		localStorage.setItem(VIEW_LOCAL_STORAGE, newValue);
		return newValue;
	});
	const [tableResize, setTableResize] = useFunctionState<{ [key: string]: number }>(
		JSON.parse(localStorage.getItem('table_opportunity_resize') || '{}'),
		({ newValue }) => {
			localStorage.setItem('table_opportunity_resize', JSON.stringify(newValue));
			return newValue;
		}
	);
	const { hiddenColumns, sortColumns, filterResult, setFilterResult, setSortColumns, setHiddenColumns } = React.useContext(TableContext);
	const users = useRecoilValue(AUsers);
	const history = useHistory();
	// a new object re-runs the query; pagination is unset when the kanban is opened first (no Pagination mounted)
	const refresh = () => setPagination(pagination => ({ ...(pagination ?? { currentPage: 1, step: DEFAULT_STEP, offset: 0 }) }));

	// re-reads the row rather than patching it locally: revenue is computed server side
	const reloadRow = (id: number) => getOpportunity(id).then(o => setOpportunities(os => os.map(r => r.id === id ? { ...o, count: r.count } : r)));
	const updateOpportunityWrapper = React.useCallback((id: number, params: QueryUpdateOpportunity) => updateOpportunity(id, params).then(() => reloadRow(id)), []);
	const updateTagsWrapper = React.useCallback((id: number, body: { ids_delete?: number[], ids_add?: number[] }) =>
		updateTags(TagType.OPPORTUNITY, { ressource_id: id, ...body }).then(() => reloadRow(id)), []);

	const openOpportunity = (id: number) => {
		history.push({ search: `?id=${id}` });
		setModalState({ isOpen: true, data: { id } });
	};

	React.useEffect(() => {
		setHiddenColumns(JSON.parse(localStorage.getItem(OPPORTUNITY_HIDDEN_LOCAL_STORAGE) || '[]'));
		setSortColumns(JSON.parse(localStorage.getItem(OPPORTUNITY_SORT_LOCAL_STORAGE) || '[]'));
		getOpportunityFilters().then(setFilterParams);
		getOpportunityStatuses().then(setStatuses);
		getTags(TagType.OPPORTUNITY).then(setTags);
	}, []);

	React.useEffect(() => {
		const f = ({ isOpen, id }) => {
			const parsedId = parseInt(id);
			if (!isNaN(parsedId)) setModalState({ isOpen, data: { id: parsedId } });
		};
		checkUrlModalParams(f)();
		return history.listen(checkUrlModalParams(f));
	}, [history]);

	React.useEffect(() => {
		setLoadingState(LoadingStateEnum.LOADING);
		queryOpportunities({
			limit: view === 'kanban' ? KANBAN_LIMIT : pagination?.step ?? DEFAULT_STEP,
			offset: view === 'kanban' ? 0 : pagination?.offset ?? 0,
			filters: filterResult?.formatted,
			search,
			order_by: sort?.id,
			descending: sort?.desc,
		})
			.then(setOpportunities)
			.catch(console.error)
			.finally(() => setLoadingState(LoadingStateEnum.LOADED));
		setSelected(new Set());
	}, [pagination, sort, filterResult, search, view]);

	React.useEffect(() => {
		props.setToolBarState({
			title: translateToString('opportunities'),
			bottomLeftToolbarComponent: <ToolbarBox>
				<ToolbarFilterButton
					activeFilters={filterTreeLength(filterResult?.formatted)}
					onClick={() => setFilterState(state => ({ ...state, openSummary: true, filterOpen: true }))}
					onDeleteFilter={() => setFilterResult(DEFAULT_FILTER_RESULT)}
				/>
				{selected.size > 0 && <FlexDiv gap='10px'>
					<DefaultTextDiv height='30px'>
						<SelectionActionContainer>
							<Restricted to={{ objectAction: 'DeleteOpportunity' }}>
								<SelectionAction onClick={() => setIsBulkDeleteOpen(true)} delete titleHover={translateToString('delete_selection')}>
									<Trash2 color={RedSidely} />
								</SelectionAction>
							</Restricted>
						</SelectionActionContainer>
					</DefaultTextDiv>
				</FlexDiv>}
			</ToolbarBox>,
			bottomRightToolbarComponent: <ToolbarBox>
				<IconsGroupBackground background={true}>
					<SwapImageContainer selected={view === 'list'} onClick={() => setView('list')}><Menu /></SwapImageContainer>
					<SwapImageContainer selected={view === 'kanban'} onClick={() => setView('kanban')}><LayoutGrid /></SwapImageContainer>
				</IconsGroupBackground>
				<InputSearch
					placeholder={translateToString('search')}
					name='search_opportunity'
					type='text'
					value={search}
					onChange={setSearch}
					delay={500}
				/>
				<Restricted to={{ objectAction: 'CreateOpportunity' }}>
					<Add onClick={() => setIsCreationOpen(true)} />
				</Restricted>
			</ToolbarBox>
		});
	}, [filterResult, selected, search, view]);

	const columns = React.useMemo(() => OpportunityColumns(
		users,
		openOpportunity,
		id => setCompanyState({ isOpen: true, data: id }),
		updateOpportunityWrapper,
		row => deleteOpportunity(row.id).then(refresh),
		loadingState,
		statuses,
		tags,
		updateTagsWrapper
	), [users, loadingState, statuses, tags, hiddenColumns, sortColumns]);

	const onClickFilter = React.useCallback((selectedId: FilterId) => setFilterState({ selectedId, openSummary: false, filterOpen: true }), []);
	const onSort = React.useCallback((sort: TableSortType) => setSort(sort[0]), []);
	const hasSearchOrFilters = (search?.trim().length ?? 0) > 0 || filterTreeLength(filterResult?.formatted) > 0;
	const total = Number(opportunities[0]?.count ?? 0);

	return <Restricted to={{ objectAction: 'ReadOpportunity' }} fallback={<Unhautorized />}>
		{opportunities.length > 0 && view === 'kanban' && <OpportunityKanban
			opportunities={opportunities}
			statuses={statuses}
			onOpen={openOpportunity}
			updateOpportunity={updateOpportunityWrapper}
			onUpdateError={refresh}
		/>}
		{opportunities.length > 0
			? view === 'list' && <Table
				height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
				columns={columns}
				data={opportunities}
				onSort={onSort}
				EnableResize
				initialSortBy={sort}
				setSelectedCheckbox={row => {
					const next = new Set(selected);
					if (row) {
						next.has(row.id) ? next.delete(row.id) : next.add(row.id);
					} else if (opportunities.every(o => selected.has(o.id))) {
						next.clear();
					} else {
						opportunities.forEach(o => next.add(o.id));
					}
					setSelected(next);
					return row ? next.has(row.id) : next.size > 0;
				}}
				isIdSelected={row => row ? selected.has(row.id) : opportunities.length > 0 && opportunities.every(o => selected.has(o.id))}
				hiddenColumns={hiddenColumns}
				sortColumns={sortColumns}
				onClickFilter={onClickFilter}
				onResize={value => Object.keys(value).length > 0 && setTableResize(value)}
				resizeValue={tableResize}
			/>
			: loadingState === LoadingStateEnum.LOADED && (hasSearchOrFilters
				? <NoData
					isNoResult
					message={translateToString('No results found')}
					messageButton={<Translate id='reset_filters' />}
					onClickButton={() => {
						setFilterResult(DEFAULT_FILTER_RESULT);
						setSearch(undefined);
					}}
				/>
				: <NoData message={translateToString('no_opportunity')} />)
		}
		<ComponentLoader loadingState={loadingState} allScreen />
		{view === 'list' && <Pagination
			label={translateToString('opportunities')}
			amount={total}
			currentCount={opportunities.length}
			steps={[DEFAULT_STEP, 50, 100]}
			onChange={setPagination}
		/>}
		<OpportunityPopup
			isOpen={modalState.isOpen}
			setOpen={isOpen => {
				if (!isOpen) removeAllUrlParameters();
				setModalState({ isOpen });
			}}
			opportunityId={modalState.data?.id}
			updateOpportunity={updateOpportunityWrapper}
			tags={tags}
		/>
		<OpportunityCreation
			isOpen={isCreationOpen}
			setOpen={setIsCreationOpen}
			onCreate={o => {
				refresh();
				openOpportunity(o.opportunity_id);
			}}
		/>
		<AdvancedFilters
			permission='ReadOpportunity'
			isOpen={filterState.filterOpen ?? false}
			setOpen={filterOpen => setFilterState({ filterOpen })}
			isOpenSummary={filterState.openSummary ?? false}
			filterList={filterParams}
			filterValues={filterResult?.values}
			onChange={setFilterResult}
			selectedFilter={filterState.selectedId}
			tagType={TagType.OPPORTUNITY}
		/>
		<ClientCompany
			isOpen={companyState.isOpen}
			setOpen={isOpen => setCompanyState({ isOpen })}
			clientCompanyId={companyState.data!}
		/>
		<PopupDeletion
			isOpen={isBulkDeleteOpen}
			onClickOut={() => setIsBulkDeleteOpen(false)}
			records={selected.size}
			onValidation={() => {
				// shortcut: one DELETE per row (max one page, 100), add a bulk route if selection across pages is needed
				Promise.all([...selected].map(deleteOpportunity)).finally(refresh);
			}}
			translationKey='opportunities'
		/>
	</Restricted>;
}
